import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BooleanApiModel } from '@app/api/api-models/BooleanApiModel';
import {
  BindedFormInstanceApiModel,
  ProcessStepApiModel,
  ProcessStepResultApiModel,
  EndOfPhasePresequenceApiModel,
  EndOfProgramPresequenceApiModel,
  CreateFormInstanceApiModel,
  CheckNumberRequestApiModel,
  FormInstanceSelectorsApiModel,
  FormInstanceBaseApiModel,
  FormInstanceHeaderInfoApiModel,
  FormInstanceQuestionsInfoApiModel,
  FormInstanceSummaryInfoApiModel,
  FormInstanceDeleteResultApiModel,
  FormInstanceCollectionDeleteResultApiModel,
  FormInstanceSubjectNarrativeApiModel,
  PerformanceCategoriesResponseApiModel,
  GroupedByHeadingCategoriesApiModel,
  CreateMultipleTraineeFormInstanceApiModel,
  UpdateMultipleTraineeFormInstanceApiModel,
  FormInstanceRequestApiModel,
  MultipleTraineeFormInstanceDraftApiModel
} from '@app/api/api-models/form-instance';
import { SearchResultApiModel } from '@app/api/api-models/search/SearchResultApiModel';
import { FormInstanceDependenciesApiModel } from '@app/api/api-models/search/FormInstanceDependenciesApiModel';
import { UserListApiModel } from '@app/api/api-models/users/UserListApiModel';
import { HttpClientService } from '@app/api/api-services/http-client.service';
import { sanitizeHtml } from '@shared/utils/editor-utils';

@Injectable()
export class FormInstancesService {
  public apiUrl = 'form-instances';

  constructor(private readonly http: HttpClientService, private readonly domSanitizer: DomSanitizer) {}

  public getGeneralInfo(id: number): Observable<FormInstanceBaseApiModel> {
    return this.http
      .get<FormInstanceBaseApiModel>(`${this.apiUrl}/get-general-info/${id}`)
      .pipe(map((x) => new FormInstanceBaseApiModel().deserialize(x)));
  }

  public getHeaderInfo(id: number): Observable<FormInstanceHeaderInfoApiModel> {
    return this.http.get<FormInstanceHeaderInfoApiModel>(`${this.apiUrl}/get-header-info/${id}`);
  }

  public getQuestionsCollectingInfo(id: number): Observable<FormInstanceQuestionsInfoApiModel> {
    return this.http.get<FormInstanceQuestionsInfoApiModel>(`${this.apiUrl}/get-questions-collecting-info/${id}`);
  }

  public getSummaryInfo(id: number): Observable<FormInstanceSummaryInfoApiModel> {
    return this.http.get<FormInstanceSummaryInfoApiModel>(`${this.apiUrl}/get-summary-info/${id}`);
  }

  public getBindedFormInstance(id: number): Observable<Array<BindedFormInstanceApiModel>> {
    return this.http
      .get<Array<BindedFormInstanceApiModel>>(`${this.apiUrl}/get-binded-form-instances/${id}`)
      .pipe(map((array) => array.map((x) => new BindedFormInstanceApiModel().deserialize(x))));
  }

  public getPerformanceCategories(id: number): Observable<PerformanceCategoriesResponseApiModel> {
    return this.http.get<PerformanceCategoriesResponseApiModel>(`${this.apiUrl}/get-performance-categories/${id}`);
  }

  public getPerformanceCollecting(id: number): Observable<Array<GroupedByHeadingCategoriesApiModel>> {
    return this.http.get<Array<GroupedByHeadingCategoriesApiModel>>(`${this.apiUrl}/get-performance-collecting/${id}`);
  }

  public getAvailableFormInstances(formInstanceId: number): Observable<Array<BindedFormInstanceApiModel>> {
    return this.http
      .get<Array<BindedFormInstanceApiModel>>(`${this.apiUrl}/${formInstanceId}/available`)
      .pipe(map((array) => array.map((x) => new BindedFormInstanceApiModel().deserialize(x))));
  }

  public getNextUsers(formInstanceId: number): Observable<Array<UserListApiModel>> {
    return this.http.get<Array<UserListApiModel>>(`${this.apiUrl}/${formInstanceId}/next-users`);
  }

  public add(model: CreateFormInstanceApiModel): Observable<FormInstanceBaseApiModel> {
    return this.http.post<FormInstanceBaseApiModel>(`${this.apiUrl}/add`, model);
  }

  public addForMultiple(model: CreateMultipleTraineeFormInstanceApiModel): Observable<FormInstanceBaseApiModel> {
    return this.http.post<FormInstanceBaseApiModel>(`${this.apiUrl}/add-for-multiple-trainee`, model);
  }

  public update(
    model: FormInstanceRequestApiModel,
    processStepApiModel: ProcessStepApiModel = null
  ): Observable<FormInstanceBaseApiModel> {
    model.CurrentComment = sanitizeHtml(model.CurrentComment);
    model.CreatorNarrative = sanitizeHtml(model.CreatorNarrative);

    if (model.IsForMultipleTrainee) {
      const multipleTraineeModel = {
        ...model,
        CurrentStep: processStepApiModel
      };

      return this.http.put<FormInstanceBaseApiModel>(
        `${this.apiUrl}/update-for-multiple-trainee`,
        multipleTraineeModel
      );
    }
    return this.http.put<FormInstanceBaseApiModel>(`${this.apiUrl}/update`, model);
  }

  public submitMultipleTraineeFormInstance(
    model: FormInstanceRequestApiModel,
    processStepApiModel: ProcessStepApiModel = null
  ): Observable<ProcessStepResultApiModel> {
    model.CurrentComment = sanitizeHtml(model.CurrentComment);
    model.CreatorNarrative = sanitizeHtml(model.CreatorNarrative);

    const multipleTraineeModel = {
      ...model,
      CurrentStep: processStepApiModel
    };

    return this.http.put<ProcessStepResultApiModel>(`${this.apiUrl}/submit-for-multiple-trainee`, multipleTraineeModel);
  }

  public updateAssigneeFormMultipleTrainee(
    model: UpdateMultipleTraineeFormInstanceApiModel
  ): Observable<FormInstanceBaseApiModel> {
    return this.http.put<FormInstanceBaseApiModel>(`${this.apiUrl}/update-multiple-trainee-assignee`, model);
  }

  public getMultipleTraineeFormInstanceDrafts(
    formId: number
  ): Observable<Array<MultipleTraineeFormInstanceDraftApiModel>> {
    return this.http.get<Array<MultipleTraineeFormInstanceDraftApiModel>>(
      `${this.apiUrl}/multiple-trainee-drafts/${formId}`
    );
  }

  public deleteById(id: number, isForMultipleTrainee = false): Observable<FormInstanceDeleteResultApiModel> {
    return this.http.delete<FormInstanceDeleteResultApiModel>(`${this.apiUrl}/delete/${id}/${isForMultipleTrainee}`);
  }

  public deleteMultiple(ids: Array<number>): Observable<FormInstanceCollectionDeleteResultApiModel> {
    let queryParams = new HttpParams();
    queryParams = queryParams.appendAll({ ids: ids });

    return this.http.delete<FormInstanceCollectionDeleteResultApiModel>(`${this.apiUrl}/delete-multiple`, {
      params: queryParams
    });
  }

  public clearMultipleTraineeDraft(id: number): Observable<FormInstanceDeleteResultApiModel> {
    return this.http.delete<FormInstanceDeleteResultApiModel>(`${this.apiUrl}/clear-multiple-trainee-draft/${id}`);
  }

  public processStep(model: ProcessStepApiModel): Observable<ProcessStepResultApiModel> {
    model.Comment = sanitizeHtml(model.Comment);

    return this.http.put<ProcessStepResultApiModel>(`${this.apiUrl}/process-step`, model);
  }

  public getSummariesById(formInstanceId: number): Observable<Array<SearchResultApiModel>> {
    const params = new HttpParams({
      fromObject: {
        formInstanceId: formInstanceId.toString()
      }
    });

    return this.http.get<Array<SearchResultApiModel>>(`${this.apiUrl}/summaries`, { params: params });
  }

  public getSummariesMultipleByIds(ids: Array<number>): Observable<Array<FormInstanceDependenciesApiModel>> {
    let queryParams = new HttpParams();
    queryParams = queryParams.appendAll({ ids: ids });

    return this.http.get<Array<FormInstanceDependenciesApiModel>>(`${this.apiUrl}/summaries-multiple`, {
      params: queryParams
    });
  }

  public getSimilarNotSubmitted(
    formId: number,
    programRoleId: string,
    toUserId: string
  ): Observable<Array<SearchResultApiModel>> {
    const params = new HttpParams({
      fromObject: {
        formId: formId.toString(),
        programRoleId: programRoleId ? programRoleId : '',
        toUserId: toUserId
      }
    });

    return this.http.get<Array<SearchResultApiModel>>(`${this.apiUrl}/not-submitted`, { params: params });
  }

  public getEndOfPhasePresequences(
    programRoleId: string,
    phaseId: number | null = null
  ): Observable<EndOfPhasePresequenceApiModel> {
    const params = new HttpParams({
      fromObject: {
        programRoleId: programRoleId,
        phaseId: phaseId ? phaseId.toString() : ''
      }
    });

    return this.http.get<EndOfPhasePresequenceApiModel>(`${this.apiUrl}/end-of-phase-presequence`, { params: params });
  }

  public getEndOfProgramPresequences(
    programRoleId: string,
    formInstanceId?: number
  ): Observable<EndOfProgramPresequenceApiModel> {
    const params = new HttpParams({
      fromObject: {
        programRoleId,
        formInstanceId: formInstanceId || ''
      }
    });

    return this.http.get<EndOfProgramPresequenceApiModel>(`${this.apiUrl}/end-of-program-presequence`, {
      params: params
    });
  }

  public getNotCompletedEndOfPhases(programRoleId: string): Observable<Array<SearchResultApiModel>> {
    const params = new HttpParams({
      fromObject: {
        programRoleId: programRoleId
      }
    });

    return this.http.get<Array<SearchResultApiModel>>(`${this.apiUrl}/not-completed-end-of-phases`, { params: params });
  }

  public checkNumber(model: CheckNumberRequestApiModel): Observable<BooleanApiModel> {
    return this.http.post<BooleanApiModel>(`${this.apiUrl}/check-number`, model);
  }

  public updateSubjectNarrative(
    formInstanceId: number,
    narrative: FormInstanceSubjectNarrativeApiModel
  ): Observable<FormInstanceSubjectNarrativeApiModel> {
    return this.http.post<FormInstanceSubjectNarrativeApiModel>(
      `${this.apiUrl}/${formInstanceId}/update-subject-narrative`,
      narrative
    );
  }

  public getPdf(id: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${id}/pdf`, { responseType: 'blob' });
  }

  public getActiveSelectors(programRoleId: string): Observable<FormInstanceSelectorsApiModel> {
    const params = new HttpParams({
      fromObject: {
        programRoleId: programRoleId
      }
    });

    return this.http
      .get<FormInstanceSelectorsApiModel>(`${this.apiUrl}/active-selectors`, { params: params })
      .pipe(map((response) => new FormInstanceSelectorsApiModel().deserialize(response, this.domSanitizer)));
  }

  public getCompletedSelectors(programRoleId: string): Observable<FormInstanceSelectorsApiModel> {
    const params = new HttpParams({
      fromObject: {
        programRoleId: programRoleId
      }
    });
    return this.http
      .get<FormInstanceSelectorsApiModel>(`${this.apiUrl}/completed-selectors`, { params: params })
      .pipe(map((response) => new FormInstanceSelectorsApiModel().deserialize(response, this.domSanitizer)));
  }
}
